/**
 * Controlled vocabularies and formatters for GreenPak Trails.
 *
 * Everything a trail record can say about itself in a constrained way lives
 * here: route type, status, verification, shade, facilities, waypoint kinds
 * and the activity/best-for tags the discovery filters are built from. The
 * admin Zod schema validates against these exact lists, the filters read the
 * same lists, so a filter can never offer a value the CMS cannot store.
 *
 * The formatters exist for one reason: to stop precision being invented.
 * An authority that publishes "2.5 to 3.5 hours" must be shown as a range,
 * not silently averaged into "3 hours" — see formatTrailDuration.
 */

// ---------- Route type ----------

export const TRAIL_ROUTE_TYPES = ["LOOP", "OUT_AND_BACK", "POINT_TO_POINT", "CONNECTOR"] as const;
export type TrailRouteType = (typeof TRAIL_ROUTE_TYPES)[number];

const ROUTE_TYPE_LABELS: Record<TrailRouteType, string> = {
  LOOP: "Loop",
  OUT_AND_BACK: "Out and back",
  POINT_TO_POINT: "Point to point",
  CONNECTOR: "Connector",
};

const ROUTE_TYPE_HINTS: Record<TrailRouteType, string> = {
  LOOP: "Returns to the trailhead by a different line.",
  OUT_AND_BACK: "Returns the way it came, so the distance is walked twice.",
  POINT_TO_POINT: "Finishes somewhere other than the start — plan the return.",
  CONNECTOR: "Links two other trails rather than standing on its own.",
};

export function isTrailRouteType(value: unknown): value is TrailRouteType {
  return typeof value === "string" && (TRAIL_ROUTE_TYPES as readonly string[]).includes(value);
}

export function trailRouteTypeLabel(value: unknown): string | null {
  return isTrailRouteType(value) ? ROUTE_TYPE_LABELS[value] : null;
}

export function trailRouteTypeHint(value: unknown): string | null {
  return isTrailRouteType(value) ? ROUTE_TYPE_HINTS[value] : null;
}

// ---------- Status ----------

export const TRAIL_STATUSES = ["OPEN", "CLOSED", "RESTRICTED", "UNKNOWN"] as const;
export type TrailStatus = (typeof TRAIL_STATUSES)[number];

const STATUS_LABELS: Record<TrailStatus, string> = {
  OPEN: "Open",
  CLOSED: "Closed",
  RESTRICTED: "Restricted",
  // Phrased as the action a visitor should take, not as a confession. It
  // still claims nothing about whether the trail is open — see the meaning
  // below, which states plainly that we have not confirmed it.
  UNKNOWN: "Check before you go",
};

/**
 * UNKNOWN is not a soft "probably fine". It is the honest state for every
 * trail whose status GreenPak has not checked against the responsible
 * authority, and it is the column default — a trail existing in the CMS is
 * not evidence that it is open today.
 */
const STATUS_MEANINGS: Record<TrailStatus, string> = {
  OPEN: "The responsible authority listed this trail as open when we last checked.",
  CLOSED: "The responsible authority had this trail closed when we last checked.",
  RESTRICTED: "Access was limited when we last checked — read the notice below before setting out.",
  UNKNOWN:
    "GreenPak has not confirmed this trail's current status with the responsible authority. Access can change with weather, wildlife and local restrictions, so ask locally before you set out.",
};

/** Drives the notice styling. Deliberately not a colour name. */
const STATUS_TONE: Record<TrailStatus, "positive" | "warning" | "critical" | "neutral"> = {
  OPEN: "positive",
  CLOSED: "critical",
  RESTRICTED: "warning",
  UNKNOWN: "neutral",
};

export function isTrailStatus(value: unknown): value is TrailStatus {
  return typeof value === "string" && (TRAIL_STATUSES as readonly string[]).includes(value);
}

/** Anything unrecognised resolves to UNKNOWN, never to OPEN. */
export function normalizeTrailStatus(value: unknown): TrailStatus {
  return isTrailStatus(value) ? value : "UNKNOWN";
}

export type TrailStatusModel = {
  status: TrailStatus;
  label: string;
  meaning: string;
  tone: "positive" | "warning" | "critical" | "neutral";
  /** "Last checked 19 August 2026", or null when nobody has recorded a check. */
  lastCheckedLabel: string | null;
  source: string | null;
  alert: { text: string; source: string | null; url: string | null } | null;
};

/**
 * Presents a stored status truthfully.
 *
 * There is no live authority feed behind this, so nothing here ever says
 * "live", "currently" or "right now". The strongest claim available is what
 * was true when a person last checked, and the date is part of the claim —
 * a status with no `statusCheckedAt` renders without a date rather than
 * borrowing today's.
 */
export function buildTrailStatus(input: {
  status: unknown;
  statusSource?: string | null;
  statusCheckedAt?: Date | string | null;
  alertText?: string | null;
  alertSource?: string | null;
  alertUrl?: string | null;
}): TrailStatusModel {
  const status = normalizeTrailStatus(input.status);
  const alertText = input.alertText?.trim();
  return {
    status,
    label: STATUS_LABELS[status],
    meaning: STATUS_MEANINGS[status],
    tone: STATUS_TONE[status],
    lastCheckedLabel: formatTrailDate(input.statusCheckedAt)
      ? `Last checked ${formatTrailDate(input.statusCheckedAt)}`
      : null,
    source: input.statusSource?.trim() || null,
    alert: alertText
      ? {
          text: alertText,
          source: input.alertSource?.trim() || null,
          url: input.alertUrl?.trim() || null,
        }
      : null,
  };
}

// ---------- Verification / trust ----------

/**
 * Three stored states, one derived.
 *
 * DRAFT → nobody has checked this against a source.
 * OFFICIAL_SOURCE → the facts were taken from, and checked against, the
 *   responsible authority's own published material.
 * FIELD_VERIFIED → a GreenPak person has walked it and confirmed the record.
 *
 * "Stale / needs reverification" is deliberately NOT a fourth stored state.
 * It is a function of how old `sourceCheckedAt` is, so it can never drift out
 * of step with the date beside it, and no editor has to remember to demote a
 * record. See buildTrailTrust.
 */
export const TRAIL_VERIFICATION_STATES = ["DRAFT", "OFFICIAL_SOURCE", "FIELD_VERIFIED"] as const;
export type TrailVerificationState = (typeof TRAIL_VERIFICATION_STATES)[number];

const VERIFICATION_LABELS: Record<TrailVerificationState, string> = {
  DRAFT: "Unverified draft",
  OFFICIAL_SOURCE: "Official-source verified",
  FIELD_VERIFIED: "GreenPak field checked",
};

const VERIFICATION_MEANINGS: Record<TrailVerificationState, string> = {
  DRAFT: "This record has not yet been checked against a responsible authority.",
  OFFICIAL_SOURCE: "The facts on this page were taken from the responsible authority's own published material.",
  FIELD_VERIFIED: "A GreenPak team member has walked this trail and confirmed the details on this page.",
};

export function isTrailVerificationState(value: unknown): value is TrailVerificationState {
  return (
    typeof value === "string" && (TRAIL_VERIFICATION_STATES as readonly string[]).includes(value)
  );
}

export function normalizeTrailVerificationState(value: unknown): TrailVerificationState {
  return isTrailVerificationState(value) ? value : "DRAFT";
}

/** How long an official-source check stands before it wants redoing. */
export const TRAIL_SOURCE_FRESHNESS_DAYS = 365;

export type TrailTrustBadge = {
  key: "official-source" | "field-checked" | "last-checked" | "needs-recheck";
  label: string;
  detail: string | null;
};

export type TrailTrustModel = {
  state: TrailVerificationState;
  stateLabel: string;
  stateMeaning: string;
  /** True once an OFFICIAL_SOURCE check is older than the freshness window. */
  stale: boolean;
  badges: TrailTrustBadge[];
  sourceOrganization: string | null;
  sourceUrl: string | null;
};

export function buildTrailTrust(
  input: {
    verificationState: unknown;
    sourceOrganization?: string | null;
    sourceUrl?: string | null;
    sourceCheckedAt?: Date | string | null;
    fieldCheckedAt?: Date | string | null;
  },
  now: Date = new Date(),
): TrailTrustModel {
  const state = normalizeTrailVerificationState(input.verificationState);
  const sourceCheckedLabel = formatTrailDate(input.sourceCheckedAt);
  const fieldCheckedLabel = formatTrailDate(input.fieldCheckedAt);
  const sourceOrganization = input.sourceOrganization?.trim() || null;

  const checkedMs = toDate(input.sourceCheckedAt)?.getTime();
  const stale =
    state !== "DRAFT" &&
    checkedMs !== undefined &&
    now.getTime() - checkedMs > TRAIL_SOURCE_FRESHNESS_DAYS * 24 * 60 * 60 * 1000;

  const badges: TrailTrustBadge[] = [];
  if (state === "OFFICIAL_SOURCE" || state === "FIELD_VERIFIED") {
    badges.push({
      key: "official-source",
      label: VERIFICATION_LABELS.OFFICIAL_SOURCE,
      detail: sourceOrganization,
    });
  }
  if (state === "FIELD_VERIFIED") {
    badges.push({
      key: "field-checked",
      label: VERIFICATION_LABELS.FIELD_VERIFIED,
      detail: fieldCheckedLabel,
    });
  }
  if (sourceCheckedLabel) {
    badges.push({ key: "last-checked", label: `Last checked ${sourceCheckedLabel}`, detail: null });
  }
  if (stale) {
    badges.push({
      key: "needs-recheck",
      label: "Due for rechecking",
      detail: `Our last source check is more than ${Math.round(TRAIL_SOURCE_FRESHNESS_DAYS / 30)} months old.`,
    });
  }

  return {
    state,
    stateLabel: VERIFICATION_LABELS[state],
    stateMeaning: VERIFICATION_MEANINGS[state],
    stale,
    badges,
    sourceOrganization,
    sourceUrl: input.sourceUrl?.trim() || null,
  };
}

// ---------- Experience vocabularies ----------

export const TRAIL_SHADE_LEVELS = ["EXPOSED", "PARTIAL", "SHADED"] as const;
export type TrailShadeLevel = (typeof TRAIL_SHADE_LEVELS)[number];

const SHADE_LABELS: Record<TrailShadeLevel, string> = {
  EXPOSED: "Little shade",
  PARTIAL: "Shade in places",
  SHADED: "Mostly shaded",
};

export function trailShadeLabel(value: unknown): string | null {
  return typeof value === "string" && (TRAIL_SHADE_LEVELS as readonly string[]).includes(value)
    ? SHADE_LABELS[value as TrailShadeLevel]
    : null;
}

/**
 * Two states only. Null — the column default — means "not verified", and it
 * is a different thing from NONE. Collapsing them would turn "we have not
 * checked whether there is drinking water" into "there is no drinking water",
 * which is exactly the class of invented amenity fact this product must not
 * produce.
 */
export const TRAIL_FACILITY_STATES = ["AVAILABLE", "NONE"] as const;
export type TrailFacilityState = (typeof TRAIL_FACILITY_STATES)[number];

export const TRAIL_FACILITIES = [
  { key: "facilityWater", label: "Drinking water" },
  { key: "facilityToilets", label: "Toilets" },
  { key: "facilityFood", label: "Food or drink" },
  { key: "facilityShelter", label: "Shelter or rest points" },
] as const;

export type TrailFacilityKey = (typeof TRAIL_FACILITIES)[number]["key"];

export type TrailFacilityModel = { key: TrailFacilityKey; label: string; available: boolean };

/** Only facilities somebody actually checked. Nulls are dropped, not shown. */
export function buildTrailFacilities(
  row: Partial<Record<TrailFacilityKey, unknown>>,
): TrailFacilityModel[] {
  const out: TrailFacilityModel[] = [];
  for (const facility of TRAIL_FACILITIES) {
    const value = row[facility.key];
    if (value !== "AVAILABLE" && value !== "NONE") continue;
    out.push({ key: facility.key, label: facility.label, available: value === "AVAILABLE" });
  }
  return out;
}

export const TRAIL_WAYPOINT_KINDS = [
  "TRAILHEAD",
  "VIEWPOINT",
  "JUNCTION",
  "FACILITY",
  "SUMMIT",
  "END",
  "WAYPOINT",
] as const;
export type TrailWaypointKind = (typeof TRAIL_WAYPOINT_KINDS)[number];

const WAYPOINT_KIND_LABELS: Record<TrailWaypointKind, string> = {
  TRAILHEAD: "Trailhead",
  VIEWPOINT: "Viewpoint",
  JUNCTION: "Junction",
  FACILITY: "Facility",
  SUMMIT: "High point",
  END: "End point",
  WAYPOINT: "Waypoint",
};

export function trailWaypointKindLabel(value: unknown): string {
  return typeof value === "string" && (TRAIL_WAYPOINT_KINDS as readonly string[]).includes(value)
    ? WAYPOINT_KIND_LABELS[value as TrailWaypointKind]
    : WAYPOINT_KIND_LABELS.WAYPOINT;
}

/**
 * Filter vocabularies. Kept short and honest: every one of these has to be
 * something an editor can defensibly assert about a trail, because each one
 * becomes a filter a visitor trusts.
 */
export const TRAIL_ACTIVITY_TYPES = [
  "HIKING",
  "TRAIL_RUNNING",
  "MOUNTAIN_BIKING",
  "BIRDWATCHING",
  "NATURE_WALK",
] as const;
export type TrailActivityType = (typeof TRAIL_ACTIVITY_TYPES)[number];

const ACTIVITY_LABELS: Record<TrailActivityType, string> = {
  HIKING: "Hiking",
  TRAIL_RUNNING: "Trail running",
  MOUNTAIN_BIKING: "Mountain biking",
  BIRDWATCHING: "Birdwatching",
  NATURE_WALK: "Nature walk",
};

export function trailActivityLabel(value: string): string {
  return ACTIVITY_LABELS[value as TrailActivityType] ?? value;
}

export const TRAIL_BEST_FOR = [
  "FIRST_TIME_HIKERS",
  "FAMILIES",
  "SUNRISE",
  "SUNSET",
  "CITY_VIEWS",
  "WILDLIFE",
  "FITNESS_TRAINING",
  "QUIET_TRAILS",
] as const;
export type TrailBestFor = (typeof TRAIL_BEST_FOR)[number];

const BEST_FOR_LABELS: Record<TrailBestFor, string> = {
  FIRST_TIME_HIKERS: "First-time hikers",
  FAMILIES: "Families",
  SUNRISE: "Sunrise",
  SUNSET: "Sunset",
  CITY_VIEWS: "City views",
  WILDLIFE: "Wildlife",
  FITNESS_TRAINING: "Fitness training",
  QUIET_TRAILS: "Quieter trails",
};

export function trailBestForLabel(value: string): string {
  return BEST_FOR_LABELS[value as TrailBestFor] ?? value;
}

// ---------- Formatters ----------

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "19 August 2026", in UTC so the rendered date never shifts by timezone. */
export function formatTrailDate(value: Date | string | null | undefined): string | null {
  const date = toDate(value);
  if (!date) return null;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/**
 * A duration, preserving whatever precision the source actually had.
 *
 * A range stays a range. CDA publishes "2.5 to 3.5 hours" for Trail 5, and
 * turning that into "3 hours" would invent a precision the authority did not
 * claim — the explicit rule for this product. Equal bounds mean the source
 * gave one figure, and it prints as one figure.
 */
export function formatTrailDuration(
  minMinutes: number | null | undefined,
  maxMinutes: number | null | undefined,
): string | null {
  const min = typeof minMinutes === "number" && minMinutes > 0 ? minMinutes : null;
  const max = typeof maxMinutes === "number" && maxMinutes > 0 ? maxMinutes : null;
  const low = min ?? max;
  const high = max ?? min;
  if (low === null || high === null) return null;
  if (low === high) return formatMinutes(low);
  // A range only drops the unit from its low end when both ends are clean
  // hours or half-hours ("2.5–3.5 hr"). Otherwise both ends are spelled out:
  // the compact form turned 75-110 minutes into "1:15–1 hr 50 min", which
  // reads as a clock time rather than a duration.
  if (isRoundHours(low) && isRoundHours(high)) {
    return `${formatMinutesShort(low)}–${formatMinutes(high)}`;
  }
  return `${formatMinutes(low)} – ${formatMinutes(high)}`;
}

/** Whole hours or half hours — the shapes that survive dropping their unit. */
function isRoundHours(total: number): boolean {
  const minutes = total % 60;
  return minutes === 0 || minutes === 30;
}

function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  if (minutes === 30) return `${hours}.5 hr`;
  return `${hours} hr ${minutes} min`;
}

/** The low end of a range drops the unit — "2.5–3.5 hr", not "2.5 hr–3.5 hr". */
function formatMinutesShort(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours}`;
  if (minutes === 30) return `${hours}.5`;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

export function formatTrailDistance(km: number | null | undefined): string | null {
  if (typeof km !== "number" || !Number.isFinite(km) || km <= 0) return null;
  // One decimal below 10 km, whole numbers above — the precision these are
  // ever actually published to.
  return km < 10 ? `${round1(km)} km` : `${Math.round(km)} km`;
}

export function formatTrailElevation(metres: number | null | undefined): string | null {
  if (typeof metres !== "number" || !Number.isFinite(metres) || metres <= 0) return null;
  return `${Math.round(metres)} m`;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
